"use client"

import { LinkExternal01, XClose } from "@untitledui/icons"
import { useState } from "react"

import { SuggestionAcceptModal } from "@/components/admin/collabs/suggestion-accept-modal"
import { SuggestionStatusBadge } from "@/components/admin/collabs/suggestion-status-badge"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Link } from "@/components/ui/link"
import { formatDate } from "@/lib/utils/"
import { CollabSuggestionStatusEnum } from "@/types/contents"
import type { CollabSuggestion } from "@/types/contents"

/**
 * One suggestion. The contributor's note is the thing a curator is
 * deciding on, so it carries the visual weight — the link and post
 * preview are supporting material, not the point.
 *
 * `onAccept`/`onReject` are optional so the same card can render
 * read-only (e.g. the collab show page's preview) as well as the
 * moderation queue's actionable rows.
 */
export function SuggestionCard({
  suggestion,
  onAccept,
  onReject,
}: {
  suggestion: CollabSuggestion
  onAccept?: (id: number) => void
  onReject?: (id: number) => void
}) {
  const [confirmingAccept, setConfirmingAccept] = useState(false)
  const isPending = suggestion.status === CollabSuggestionStatusEnum.pending
  const isActionable = isPending && onAccept && onReject

  return (
    <div className="border-secondary bg-primary flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Avatar
            name={suggestion.contributor_name}
            avatarUrl={suggestion.contributor_avatar_url}
            size="sm"
          />
          <div className="flex flex-col">
            <span className="text-primary text-sm font-semibold">
              {suggestion.contributor_name}
            </span>
            <span className="text-tertiary text-xs">
              {formatDate(suggestion.created_at)}
            </span>
          </div>
        </div>
        <SuggestionStatusBadge status={suggestion.status} />
      </div>

      {suggestion.notes && (
        <p className="text-primary text-md leading-relaxed">
          {suggestion.notes}
        </p>
      )}

      {suggestion.url && (
        <Link
          href={suggestion.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="tertiary"
          className="inline-flex w-fit items-center gap-1 text-xs"
        >
          <LinkExternal01 aria-hidden="true" className="size-3" />
          <span className="max-w-xs truncate">{suggestion.url}</span>
        </Link>
      )}

      {suggestion.post && (
        <div className="border-secondary bg-secondary/40 flex items-center gap-3 rounded-lg border p-2">
          {suggestion.post.thumbnail_url && (
            <img
              src={suggestion.post.thumbnail_url}
              alt=""
              className="size-10 shrink-0 rounded-md object-cover"
            />
          )}
          <div className="flex min-w-0 flex-col">
            <span className="text-secondary truncate text-xs font-medium">
              {suggestion.post.title}
            </span>
            {suggestion.post.creator && (
              <span className="text-tertiary truncate text-xs">
                {suggestion.post.creator}
              </span>
            )}
          </div>
        </div>
      )}

      {isActionable && (
        <div className="flex items-center gap-2 pt-1">
          <Button
            color="secondary"
            size="sm"
            iconLeading={XClose}
            onClick={() => onReject(suggestion.id)}
          >
            Reject
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() => setConfirmingAccept(true)}
          >
            Accept
          </Button>
        </div>
      )}

      {isActionable && (
        <SuggestionAcceptModal
          isOpen={confirmingAccept}
          onOpenChange={setConfirmingAccept}
          onConfirm={() => onAccept(suggestion.id)}
        />
      )}
    </div>
  )
}
